import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import {
  STELLEN_STATUS,
  EINRICHTUNGSTYPEN,
  BUNDESLAENDER,
  QUALIFIKATIONEN,
  ARBEITSBEREICHE,
  ARBEITSMODELLE,
  SPRACHLEVEL,
  EINSPRINGEN_HAEUFIGKEIT,
  WOCHENEND_FREQUENZ,
  FEEDBACK_RHYTHMUS,
  BENEFITS_OPTIONEN,
} from '../../lib/constants'
import type { Stelle } from '../../lib/types'
import SliderProfile from './SliderProfile'
import './AdminPages.css'

interface MatchForStelle {
  id: string
  score: number
  kategorie: string
  pflegekraefte: { id: string; vorname: string; nachname: string; qualifikation: string | null; bundesland: string | null }
}

export default function StellenDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [stelle, setStelle] = useState<Stelle | null>(null)
  const [matches, setMatches] = useState<MatchForStelle[]>([])
  const [loading, setLoading] = useState(true)
  const [notizen, setNotizen] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (id) loadData()
  }, [id])

  async function loadData() {
    setLoading(true)
    const { data } = await supabase.from('stellen').select('*, arbeitgeber(id, name)').eq('id', id!).single()
    if (data) {
      setStelle(data as unknown as Stelle)
      setNotizen(data.interne_notizen || '')
    }
    const { data: matchData } = await supabase
      .from('matches')
      .select('id, score, kategorie, pflegekraefte!inner(id, vorname, nachname, qualifikation, bundesland)')
      .eq('stelle_id', id!)
      .order('score', { ascending: false })
      .limit(30)
    if (matchData) setMatches(matchData as unknown as MatchForStelle[])
    setLoading(false)
  }

  async function updateStatus(newStatus: string) {
    await supabase.from('stellen').update({ status: newStatus }).eq('id', id!)
    setStelle((prev) => (prev ? { ...prev, status: newStatus } : null))
  }

  async function saveNotizen() {
    setSaving(true)
    await supabase.from('stellen').update({ interne_notizen: notizen }).eq('id', id!)
    setSaving(false)
  }

  const label = (list: readonly { value: string; label: string }[], val: string | null) =>
    list.find((i) => i.value === val)?.label || val || '–'
  const haeuf = (val: string | null | undefined) =>
    val === 'ja' ? '✅ Ja, regelmäßig' : val === 'selten' ? '🟡 Selten' : val === 'nein' ? '❌ Nein' : '–'
  const jaNein = (val: unknown) => (val === true ? '✅ Ja' : val === false ? '❌ Nein' : '–')

  if (loading) return <div className="table-loading"><div className="loading-spinner" /></div>
  if (!stelle) return <div className="admin-page"><p>Stelle nicht gefunden.</p></div>

  const statusColor = STELLEN_STATUS.find((s) => s.value === stelle.status)?.color || '#94A3B8'
  const fakten = (stelle.fakten || {}) as Record<string, unknown>
  const benefitLabel = (v: string) => BENEFITS_OPTIONEN.find((b) => b.value === v)?.label || v

  return (
    <div className="admin-page animate-fade-in-up">
      <button className="back-button" onClick={() => navigate('/admin/stellen')}>← Zurück zur Liste</button>

      <div className="detail-header">
        <div className="detail-title-row">
          <h1>{stelle.titel}</h1>
          <span className="status-badge status-badge--lg" style={{ '--badge-color': statusColor } as React.CSSProperties}>
            {STELLEN_STATUS.find((s) => s.value === stelle.status)?.label || stelle.status}
          </span>
        </div>
        <p className="page-subtitle">
          {stelle.arbeitgeber ? stelle.arbeitgeber.name : 'Kein Arbeitgeber'} · {stelle.ort || 'Ort unbekannt'}
          {stelle.quelle && <> · Quelle: {stelle.quelle}</>}
        </p>
      </div>

      <div className="status-buttons" style={{ marginBottom: 'var(--space-6)' }}>
        {STELLEN_STATUS.map((s) => (
          <button
            key={s.value}
            className={`status-btn ${stelle.status === s.value ? 'status-btn--active' : ''}`}
            style={{ '--btn-color': s.color } as React.CSSProperties}
            onClick={() => updateStatus(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="detail-grid">
        <div className="detail-section">
          <h3>📍 Standort</h3>
          <dl className="detail-dl">
            <dt>Ort</dt><dd>{stelle.ort || '–'}</dd>
            <dt>PLZ</dt><dd>{stelle.plz || '–'}</dd>
            <dt>Bundesland</dt><dd>{label(BUNDESLAENDER, stelle.bundesland)}</dd>
            <dt>Einrichtungstyp</dt><dd>{label(EINRICHTUNGSTYPEN, stelle.einrichtungstyp)}</dd>
          </dl>
        </div>

        <div className="detail-section">
          <h3>📋 Anforderungen</h3>
          <dl className="detail-dl">
            <dt>Qualifikation</dt><dd>{label(QUALIFIKATIONEN, stelle.erforderliche_qualifikation)}</dd>
            <dt>Einsatzbereich</dt><dd>{label(ARBEITSBEREICHE, stelle.einsatzbereich)}</dd>
            <dt>Mind. Erfahrung</dt><dd>{stelle.mindestberufserfahrung != null ? `${stelle.mindestberufserfahrung} Jahre` : '–'}</dd>
            <dt>Anerkennung</dt><dd>{haeuf(stelle.anerkennung_erforderlich)}</dd>
            <dt>Sprachlevel</dt><dd>{label(SPRACHLEVEL, stelle.sprachlevel)}</dd>
          </dl>
        </div>

        <div className="detail-section">
          <h3>⏰ Arbeitszeit & Konditionen</h3>
          <dl className="detail-dl">
            <dt>Arbeitsmodell</dt><dd>{label(ARBEITSMODELLE, stelle.arbeitsmodell)}</dd>
            <dt>Wochenstunden</dt>
            <dd>{stelle.wochenstunden_min || stelle.wochenstunden_max ? `${stelle.wochenstunden_min ?? '?'} – ${stelle.wochenstunden_max ?? '?'} Std.` : '–'}</dd>
            <dt>Gehalt</dt>
            <dd>{stelle.gehalt_min || stelle.gehalt_max ? `${stelle.gehalt_min?.toLocaleString('de-DE') || '?'} – ${stelle.gehalt_max?.toLocaleString('de-DE') || '?'} €` : '–'}</dd>
            <dt>Zuschläge</dt><dd>{stelle.zuschlaege || '–'}</dd>
            <dt>Startdatum</dt><dd>{stelle.startdatum ? new Date(stelle.startdatum).toLocaleDateString('de-DE') : '–'}</dd>
            <dt>Flexibler Start</dt><dd>{jaNein(stelle.flexibler_start)}</dd>
            <dt>Probezeit</dt><dd>{stelle.probezeit || '–'}</dd>
            <dt>Befristung</dt><dd>{jaNein(stelle.befristung)}</dd>
          </dl>
        </div>

        <div className="detail-section">
          <h3>🌙 Schicht-Anforderungen</h3>
          <dl className="detail-dl">
            <dt>Nachtdienst</dt><dd>{haeuf(stelle.nachtdienst_erforderlich)}</dd>
            <dt>Wochenende</dt><dd>{haeuf(stelle.wochenenddienst_erforderlich)}</dd>
            <dt>Geteilte Dienste</dt><dd>{haeuf(stelle.geteilte_dienste)}</dd>
            <dt>Rufbereitschaft</dt><dd>{haeuf(stelle.rufbereitschaft)}</dd>
          </dl>
        </div>
      </div>

      {/* Benefits */}
      {stelle.benefits && stelle.benefits.length > 0 && (
        <div className="detail-section">
          <h3>🎁 Benefits</h3>
          <div className="chip-list">
            {stelle.benefits.map((b) => <span key={b} className="chip chip--teal">{benefitLabel(b)}</span>)}
          </div>
        </div>
      )}

      {/* Faktenfelder */}
      {Object.keys(fakten).length > 0 && (
        <div className="detail-section">
          <h3>🔍 Faktenfelder (Plausibilisierung)</h3>
          <dl className="detail-dl">
            {fakten.dienstplan_vorlauf_wochen != null && <><dt>Dienstplan-Vorlauf</dt><dd>{String(fakten.dienstplan_vorlauf_wochen)} Wochen</dd></>}
            {fakten.ueberstunden_pro_monat != null && <><dt>Überstunden/Monat</dt><dd>{String(fakten.ueberstunden_pro_monat)} Std.</dd></>}
            {fakten.einspringen_haeufigkeit != null && <><dt>Einspringen</dt><dd>{label(EINSPRINGEN_HAEUFIGKEIT, fakten.einspringen_haeufigkeit as string)}</dd></>}
            {fakten.wochenend_frequenz != null && <><dt>Wochenend-Frequenz</dt><dd>{label(WOCHENEND_FREQUENZ, fakten.wochenend_frequenz as string)}</dd></>}
            {fakten.betreuungsschluessel != null && <><dt>Betreuungsschlüssel</dt><dd>{String(fakten.betreuungsschluessel)}</dd></>}
            {'digitale_doku' in fakten && <><dt>Digitale Doku</dt><dd>{jaNein(fakten.digitale_doku)}</dd></>}
            {'hilfskraefte_vorhanden' in fakten && <><dt>Hilfskräfte</dt><dd>{jaNein(fakten.hilfskraefte_vorhanden)}</dd></>}
            {'hebehilfen_vorhanden' in fakten && <><dt>Hebehilfen</dt><dd>{jaNein(fakten.hebehilfen_vorhanden)}</dd></>}
            {'teammeetings' in fakten && <><dt>Teammeetings</dt><dd>{jaNein(fakten.teammeetings)}</dd></>}
            {fakten.feedback_rhythmus != null && <><dt>Feedbackgespräche</dt><dd>{label(FEEDBACK_RHYTHMUS, fakten.feedback_rhythmus as string)}</dd></>}
            {'praxisanleitung' in fakten && <><dt>Praxisanleitung</dt><dd>{jaNein(fakten.praxisanleitung)}</dd></>}
            {fakten.onboarding_dauer_wochen != null && <><dt>Einarbeitung</dt><dd>{String(fakten.onboarding_dauer_wochen)} Wochen</dd></>}
            {'feste_ansprechperson' in fakten && <><dt>Feste Ansprechperson</dt><dd>{jaNein(fakten.feste_ansprechperson)}</dd></>}
            {'hospitation_moeglich' in fakten && <><dt>Hospitation</dt><dd>{jaNein(fakten.hospitation_moeglich)}</dd></>}
            {fakten.umzugshilfe != null && <><dt>Umzugshilfe</dt><dd>{String(fakten.umzugshilfe)}</dd></>}
            {fakten.anerkennungshilfe != null && <><dt>Anerkennungshilfe</dt><dd>{String(fakten.anerkennungshilfe)}</dd></>}
          </dl>
        </div>
      )}

      {/* Slider-Profil */}
      <div className="detail-section">
        <h3>⭐ Arbeitsbedingungen-Profil (Slider)</h3>
        <SliderProfile answers={stelle.slider_antworten} side="employer" />
      </div>

      {stelle.beschreibung && (
        <div className="detail-section">
          <h3>📝 Beschreibung</h3>
          <div className="detail-freetext"><p style={{ whiteSpace: 'pre-wrap' }}>{stelle.beschreibung}</p></div>
        </div>
      )}

      <div className="detail-section">
        <h3>🎯 Matches ({matches.length})</h3>
        {matches.length > 0 ? (
          <div className="top-matches-list">
            {matches.map((m) => (
              <div key={m.id} className="top-match-item" onClick={() => navigate(`/admin/pflegekraefte/${m.pflegekraefte.id}`)}>
                <div className="top-match-score" style={{ color: m.score >= 80 ? '#10B981' : m.score >= 60 ? '#3B82F6' : '#F59E0B' }}>
                  {m.score}
                </div>
                <div className="top-match-info">
                  <span className="top-match-name">👤 {m.pflegekraefte.vorname} {m.pflegekraefte.nachname}</span>
                  <span className="top-match-stelle">
                    {label(QUALIFIKATIONEN, m.pflegekraefte.qualifikation)} · {label(BUNDESLAENDER, m.pflegekraefte.bundesland)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-hint" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
            Noch keine Matches. Starten Sie das Matching über die Matching-Seite.
          </p>
        )}
      </div>

      <div className="detail-section">
        <h3>🗒️ Interne Notizen</h3>
        <textarea className="notes-textarea" value={notizen} onChange={(e) => setNotizen(e.target.value)} rows={4} placeholder="Interne Notizen..." />
        <button className="btn btn--secondary" onClick={saveNotizen} disabled={saving}>
          {saving ? 'Speichern...' : '💾 Notizen speichern'}
        </button>
      </div>
    </div>
  )
}
